/**
 * AIContextInspector
 *
 * Floating reference panel showing all variables and MCP tools available to an AI node.
 * Renders as a small "{ } Variables" button that opens a portal popover — does not
 * displace surrounding form content.
 */
import React, { useState, useRef, useCallback, useEffect } from "react";
import ReactDOM from "react-dom";

interface Props {
  mcpToolNames?: string[];
}

const BUILTIN_VARS = [
  {
    scope: "conversation",
    color: "blue",
    vars: [
      { name: "conversation.id", desc: "Unique ID for this conversation" },
      { name: "conversation.channel", desc: "Channel: web, sms, whatsapp, email" },
      { name: "conversation.subject", desc: "Subject or initial visitor message" },
      { name: "conversation.startedAt", desc: "ISO timestamp when conversation started" },
      { name: "conversation.referrer", desc: "Page URL when visitor started the chat" },
      { name: "conversation.siteId", desc: "Site ID this conversation belongs to" },
      { name: "conversation.deploymentId", desc: "Chat button (deployment) that started this conversation" }
    ]
  },
  {
    scope: "contact",
    color: "purple",
    vars: [
      { name: "contact.id", desc: "Contact record ID" },
      { name: "contact.firstName", desc: "First name (if known)" },
      { name: "contact.lastName", desc: "Last name (if known)" },
      { name: "contact.email", desc: "Email address (if known)" },
      { name: "contact.phone", desc: "Phone number (if known)" },
      { name: "contact.company", desc: "Company name (if known)" }
    ]
  },
  {
    scope: "workflow",
    color: "green",
    vars: [
      {
        name: "workflow.*",
        desc: "Custom properties set by Set Data nodes earlier in this workflow. Use the exact property name you defined."
      }
    ]
  }
];

const SCOPE_COLORS: Record<string, string> = {
  blue: "text-blue-700 bg-blue-50 border-blue-200",
  purple: "text-purple-700 bg-purple-50 border-purple-200",
  green: "text-green-700 bg-green-50 border-green-200"
};

export default function AIContextInspector({ mcpToolNames = [] }: Props) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);

  const handleToggle = useCallback(() => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({
        top: r.bottom + 6,
        right: window.innerWidth - r.right
      });
    }
    setOpen(v => !v);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div className="flex justify-end mt-2">
      <button
        ref={btnRef}
        type="button"
        onClick={handleToggle}
        className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1.5 px-2 py-1 rounded border border-slate-200 hover:border-slate-300 transition-colors bg-white"
        aria-expanded={open}
        aria-label="Toggle variable reference"
        title="View available variables and tools"
      >
        <span className="font-mono text-[11px] font-bold">{"{}"}</span>
        Variables
        <span className="text-[10px] opacity-60">{open ? "▲" : "▼"}</span>
      </button>

      {open && ReactDOM.createPortal(
        <>
          {/* backdrop — click to close */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          {/* panel */}
          <div
            className="fixed z-50 bg-white border border-slate-200 shadow-2xl rounded-xl w-80 max-h-[70vh] overflow-y-auto"
            style={{ top: pos.top, right: pos.right }}
            role="dialog"
            aria-label="Available variables and tools"
          >
            <div className="sticky top-0 bg-white border-b border-slate-100 px-3 py-2 flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                Variables &amp; Tools
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-slate-400 hover:text-slate-600 text-lg leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="p-3 space-y-3 text-xs">
              <p className="text-slate-500 leading-relaxed">
                Reference these in your system prompt using{" "}
                <code className="bg-slate-100 px-1 py-0.5 rounded font-mono text-slate-700">
                  {"<<variable.name>>"}
                </code>{" "}
                syntax.
              </p>

              {BUILTIN_VARS.map((group) => (
                <div key={group.scope} className={`rounded-lg border p-2 ${SCOPE_COLORS[group.color]}`}>
                  <p className="font-semibold mb-1 capitalize">{group.scope}</p>
                  <ul className="space-y-1">
                    {group.vars.map((v) => (
                      <li key={v.name} className="flex flex-col">
                        <code className="font-mono font-medium">{`<<${v.name}>>`}</code>
                        <span className="text-[11px] opacity-70 mt-0.5">{v.desc}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              {mcpToolNames.length > 0 && (
                <div className="rounded-lg border border-teal-200 bg-teal-50 text-teal-700 p-2">
                  <p className="font-semibold mb-1">MCP Tools</p>
                  <ul className="space-y-0.5">
                    {mcpToolNames.map((name) => (
                      <li key={name}>
                        <code className="font-mono text-[11px]">{name}</code>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
